import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { isNative, isPluginAvailable } from './platform';

/**
 * Camera Service for ValuePlus App
 * Used for document scanning via device camera
 */

/**
 * Check if camera is available
 */
export const isCameraAvailable = () => {
  return isNative() && isPluginAvailable('Camera');
};

/**
 * Take a photo using the device camera for document scanning
 * Returns a File object that can be uploaded
 */
export const scanDocument = async () => {
  try {
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: true,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Camera,
      correctOrientation: true,
      width: 2048,
      height: 2048,
      promptLabelHeader: 'Dokument scannen',
      promptLabelCancel: 'Abbrechen',
      promptLabelPhoto: 'Aus Galerie',
      promptLabelPicture: 'Foto aufnehmen',
    });

    // Convert data URL to File object for upload
    const response = await fetch(image.dataUrl);
    const blob = await response.blob();
    const fileName = `scan_${Date.now()}.${image.format || 'jpeg'}`;
    const file = new File([blob], fileName, { type: `image/${image.format || 'jpeg'}` });

    return file;
  } catch (error) {
    if (error.message === 'User cancelled photos app') {
      return null; // User cancelled, not an error
    }
    console.error('Document scan failed:', error);
    throw error;
  }
};

/**
 * Pick a photo from gallery
 * Returns a File object that can be uploaded
 */
export const pickFromGallery = async () => {
  try {
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Photos,
      correctOrientation: true,
    });

    const response = await fetch(image.dataUrl);
    const blob = await response.blob();
    const fileName = `gallery_${Date.now()}.${image.format || 'jpeg'}`;
    const file = new File([blob], fileName, { type: `image/${image.format || 'jpeg'}` });

    return file;
  } catch (error) {
    if (error.message === 'User cancelled photos app') {
      return null;
    }
    console.error('Gallery pick failed:', error);
    throw error;
  }
};

/**
 * Choose between camera and gallery
 * Returns a File object or null if cancelled
 */
export const captureDocument = async (source = 'prompt') => {
  try {
    const cameraSource = source === 'camera' 
      ? CameraSource.Camera 
      : source === 'gallery' 
        ? CameraSource.Photos 
        : CameraSource.Prompt;

    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: true,
      resultType: CameraResultType.DataUrl,
      source: cameraSource,
      correctOrientation: true,
      width: 2048,
      height: 2048,
      promptLabelHeader: 'Dokument hochladen',
      promptLabelCancel: 'Abbrechen',
      promptLabelPhoto: 'Aus Galerie wählen',
      promptLabelPicture: 'Foto aufnehmen',
    });

    const response = await fetch(image.dataUrl);
    const blob = await response.blob();
    const fileName = `document_${Date.now()}.${image.format || 'jpeg'}`;
    const file = new File([blob], fileName, { type: `image/${image.format || 'jpeg'}` });

    return file;
  } catch (error) {
    if (error.message === 'User cancelled photos app') {
      return null;
    }
    throw error;
  }
};
